// 👨‍💼 Admin API Service
/**
 * 관리자 전용 API 서비스
 * - 전체 쿠폰 시스템 관리
 * - 사용자 통계 및 분석
 * - 추천 시스템 모니터링
 * 
 * Author: feature/yeongu/coupon-system
 * Created: 2025-01-27
 * Frontend Version: v1.3.0
 */

import { auth } from '../firebase/config';
import { getApiBaseUrl } from '../utils/apiConfig';

// API 요청 헬퍼 (프로덕션 환경 호환)
const makeAuthenticatedRequest = async (endpoint, options = {}) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('관리자 인증이 필요합니다');
    }

    // 🔧 hostname 기반 동적 API URL (프로덕션 환경 호환)
    const API_BASE_URL = getApiBaseUrl();

    const requestWithToken = async (forceRefresh = false) => {
      const token = await user.getIdToken(forceRefresh);
      return fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers,
        },
      });
    };

    let response = await requestWithToken(false);
    if (response.status === 401) {
      response = await requestWithToken(true);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const detail = errorData.detail;
      const message = (
        typeof detail === 'string'
          ? detail
          : detail?.message
      ) || `HTTP ${response.status}: ${response.statusText}`;
      const requestError = new Error(message);
      requestError.code = detail?.code;
      requestError.detail = detail;
      requestError.status = response.status;
      throw requestError;
    }

    return await response.json();
  } catch (error) {
    console.error(`Admin API Error (${endpoint}):`, error);
    throw error;
  }
};

// ===== 쿠폰 관리 =====

/**
 * 전체 쿠폰 통계 조회
 */
export const getAdminCouponStats = async () => {
  return await makeAuthenticatedRequest('/api/admin/coupons/stats');
};

/**
 * 전체 쿠폰 목록 조회 (관리자용)
 */
export const getAdminCoupons = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.status) params.append('status', filters.status);
  if (filters.source) params.append('source', filters.source);
  if (filters.user_id) params.append('user_id', filters.user_id);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.offset) params.append('offset', filters.offset.toString());

  const queryString = params.toString();
  const endpoint = `/api/admin/coupons${queryString ? `?${queryString}` : ''}`;
  
  return await makeAuthenticatedRequest(endpoint);
};

/**
 * 쿠폰 상태 변경 (관리자)
 */
export const updateCouponStatus = async (couponId, status, reason = '') => {
  return await makeAuthenticatedRequest(`/api/admin/coupons/${couponId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ 
      status,
      admin_reason: reason 
    }),
  });
};

/**
 * 쿠폰 삭제 (관리자)
 */
export const deleteCoupon = async (couponId, reason = '') => {
  return await makeAuthenticatedRequest(`/api/admin/coupons/${couponId}`, {
    method: 'DELETE',
    body: JSON.stringify({ 
      admin_reason: reason 
    }),
  });
};

/**
 * 대량 쿠폰 생성 (관리자)
 */
export const createBulkCoupons = async (couponData) => {
  return await makeAuthenticatedRequest('/api/admin/coupons/bulk', {
    method: 'POST',
    body: JSON.stringify(couponData),
  });
};

// ===== 사용자 관리 =====

/**
 * 사용자별 쿠폰 사용 통계
 */
export const getUserCouponStats = async (userId = null, period = '30d') => {
  const params = new URLSearchParams({ period });
  if (userId) params.append('user_id', userId);
  
  return await makeAuthenticatedRequest(`/api/admin/users/coupon-stats?${params.toString()}`);
};

/**
 * 활성 사용자 목록
 */
export const getActiveUsers = async (limit = 100) => {
  return await makeAuthenticatedRequest(`/api/admin/users/active?limit=${limit}`);
};

/**
 * 사용자 쿠폰 내역 조회
 */
export const getUserCouponHistory = async (userId, limit = 50) => {
  return await makeAuthenticatedRequest(`/api/admin/users/${userId}/coupons?limit=${limit}`);
};

// ===== 추천 시스템 관리 =====

/**
 * 전체 추천 시스템 통계
 */
export const getAdminReferralStats = async () => {
  return await makeAuthenticatedRequest('/api/admin/referrals/stats');
};

/**
 * 추천 코드 목록 (관리자용)
 */
export const getAdminReferralCodes = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.status) params.append('status', filters.status);
  if (filters.user_id) params.append('user_id', filters.user_id);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.offset) params.append('offset', filters.offset.toString());

  const queryString = params.toString();
  const endpoint = `/api/admin/referrals/codes${queryString ? `?${queryString}` : ''}`;
  
  return await makeAuthenticatedRequest(endpoint);
};

/**
 * 추천 관계 목록 (관리자용)
 */
export const getAdminReferrals = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.status) params.append('status', filters.status);
  if (filters.referrer_id) params.append('referrer_id', filters.referrer_id);
  if (filters.referred_id) params.append('referred_id', filters.referred_id);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.offset) params.append('offset', filters.offset.toString());

  const queryString = params.toString();
  const endpoint = `/api/admin/referrals${queryString ? `?${queryString}` : ''}`;
  
  return await makeAuthenticatedRequest(endpoint);
};

/**
 * 추천 리워드 관리
 */
export const getAdminReferralRewards = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.status) params.append('status', filters.status);
  if (filters.user_id) params.append('user_id', filters.user_id);
  if (filters.reward_type) params.append('reward_type', filters.reward_type);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.limit) params.append('limit', filters.limit.toString());

  const queryString = params.toString();
  const endpoint = `/api/admin/referrals/rewards${queryString ? `?${queryString}` : ''}`;
  
  return await makeAuthenticatedRequest(endpoint);
};

/**
 * 리워드 상태 변경
 */
export const updateRewardStatus = async (rewardId, status, reason = '') => {
  return await makeAuthenticatedRequest(`/api/admin/referrals/rewards/${rewardId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ 
      status,
      admin_reason: reason 
    }),
  });
};

// ===== 시스템 설정 =====

/**
 * 쿠폰 시스템 설정 조회
 */
export const getCouponSystemSettings = async () => {
  return await makeAuthenticatedRequest('/api/admin/settings/coupons');
};

/**
 * 쿠폰 시스템 설정 업데이트
 */
export const updateCouponSystemSettings = async (settings) => {
  return await makeAuthenticatedRequest('/api/admin/settings/coupons', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
};

/**
 * 추천 시스템 설정 조회
 */
export const getReferralSystemSettings = async () => {
  return await makeAuthenticatedRequest('/api/admin/settings/referrals');
};

/**
 * 추천 시스템 설정 업데이트
 */
export const updateReferralSystemSettings = async (settings) => {
  return await makeAuthenticatedRequest('/api/admin/settings/referrals', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
};

// ===== 슈퍼 관리자 대시보드 (서비스 안정성/비용/전환율) =====

/**
 * API 키 상태 조회 (슈퍼 관리자 전용)
 */
export const getApiKeyStatus = async () => {
  return await makeAuthenticatedRequest('/api/admin/api-keys/status');
};

/**
 * 결제 전환율 분석 (슈퍼 관리자 전용)
 */
export const getConversionAnalytics = async (period = '30d') => {
  return await makeAuthenticatedRequest(`/api/admin/analytics/conversion?period=${period}`);
};

/**
 * 리텐션 분석 (슈퍼 관리자 전용)
 */
export const getRetentionAnalytics = async () => {
  return await makeAuthenticatedRequest('/api/admin/analytics/retention');
};

/**
 * 결제 흐름 분석 (슈퍼 관리자 전용)
 */
export const getPaymentFlowAnalytics = async (period = '30d') => {
  return await makeAuthenticatedRequest(`/api/admin/analytics/payment-flow?period=${period}`);
};

/**
 * 시스템 상태 조회 (슈퍼 관리자)
 */
export const getSystemHealthStatus = async () => {
  return await makeAuthenticatedRequest('/api/admin/system/health');
};

/**
 * 관리자 권한 확인 (슈퍼 관리자 여부 포함)
 */
export const getAdminPermissions = async () => {
  return await makeAuthenticatedRequest('/api/admin/permissions');
};

/**
 * Slack 알림 테스트 (슈퍼 관리자 전용)
 */
export const testSlackAlert = async () => {
  return await makeAuthenticatedRequest('/api/admin/alerts/test', {
    method: 'POST'
  });
};

/**
 * 쿠폰 사용 트렌드 분석
 */
export const getCouponUsageTrends = async (period = '30d', granularity = 'daily') => {
  return await makeAuthenticatedRequest(`/api/admin/analytics/coupon-trends?period=${period}&granularity=${granularity}`);
};

/**
 * 추천 성과 분석
 */
export const getReferralPerformance = async (period = '30d') => {
  return await makeAuthenticatedRequest(`/api/admin/analytics/referral-performance?period=${period}`);
};

/**
 * 사용자 행동 분석
 */
export const getUserBehaviorAnalytics = async (period = '30d') => {
  return await makeAuthenticatedRequest(`/api/admin/analytics/user-behavior?period=${period}`);
};

// ===== 유틸리티 함수 =====

/**
 * 관리자 권한 확인
 */
export const checkAdminPermissions = async () => {
  try {
    return await makeAuthenticatedRequest('/api/admin/permissions');
  } catch (error) {
    return { is_admin: false, permissions: [] };
  }
};

/**
 * 에러 메시지 한글화
 */
export const getAdminErrorMessage = (error) => {
  const errorMessages = {
    'Unauthorized': '관리자 권한이 필요합니다',
    'Forbidden': '해당 작업에 대한 권한이 없습니다',
    'Not Found': '요청한 리소스를 찾을 수 없습니다',
    'Bad Request': '잘못된 요청입니다',
    'Internal Server Error': '서버 내부 오류가 발생했습니다',
    'Network Error': '네트워크 연결을 확인해주세요',
    'Timeout': '요청 시간이 초과되었습니다'
  };

  const message = error?.message || error?.toString() || '알 수 없는 오류';
  
  // 특정 에러 메시지 매칭
  for (const [key, value] of Object.entries(errorMessages)) {
    if (message.includes(key)) {
      return value;
    }
  }

  // 기본 에러 메시지 반환
  return message.includes('관리자') ? message : `관리자 작업 중 오류가 발생했습니다: ${message}`;
};

/**
 * 날짜 범위 유틸리티
 */
export const getDateRange = (period) => {
  const end = new Date();
  const start = new Date();

  switch (period) {
    case '7d':
      start.setDate(end.getDate() - 7);
      break;
    case '30d':
      start.setDate(end.getDate() - 30);
      break;
    case '90d':
      start.setDate(end.getDate() - 90);
      break;
    case '1y':
      start.setFullYear(end.getFullYear() - 1);
      break;
    default:
      start.setDate(end.getDate() - 30);
  }

  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0]
  };
};

/**
 * 데이터 내보내기
 */
export const exportData = async (type, filters = {}, format = 'csv') => {
  const params = new URLSearchParams({ format, ...filters });
  return await makeAuthenticatedRequest(`/api/admin/export/${type}?${params.toString()}`);
};

/**
 * 시스템 상태 확인
 */
export const getSystemHealth = async () => {
  return await makeAuthenticatedRequest('/api/admin/system/health');
};

// ============================================================================
// 그로스 메트릭 API
// ============================================================================

/**
 * 핵심 그로스 메트릭 조회
 */
export const getGrowthMetrics = async (period = '7d') => {
  return await makeAuthenticatedRequest(`/api/admin/analytics/growth-metrics?period=${period}`);
};

/**
 * 주간 리포트 조회
 */
export const getWeeklyReport = async (weeks = 12) => {
  return await makeAuthenticatedRequest(`/api/admin/analytics/weekly-report?weeks=${weeks}`);
};

/**
 * 리텐션 코호트 분석
 */
export const getRetentionCohort = async (weeks = 8) => {
  return await makeAuthenticatedRequest(`/api/admin/analytics/retention-cohort?weeks=${weeks}`);
};

// ============================================================================
// API 키 상세 관리
// ============================================================================

/**
 * API 키 상세 상태 조회
 */
export const getDetailedApiKeyStatus = async () => {
  return await makeAuthenticatedRequest('/api/admin/api-keys/detailed');
};

/**
 * API 키 쿨다운 리셋
 */
export const resetApiKeyCooldown = async (provider, keyIndex = null) => {
  const params = new URLSearchParams({ provider });
  if (keyIndex !== null) {
    params.append('key_index', keyIndex);
  }
  return await makeAuthenticatedRequest(`/api/admin/api-keys/reset-cooldown?${params.toString()}`, {
    method: 'POST'
  });
};

/**
 * API 사용량 통계 조회 (실시간 + 누적)
 */
export const getApiUsageStats = async () => {
  return await makeAuthenticatedRequest('/api/admin/api-keys/usage');
};

/**
 * 일별 API 사용량 통계 조회
 */
export const getDailyApiUsage = async (days = 7) => {
  return await makeAuthenticatedRequest(`/api/admin/api-keys/daily-usage?days=${days}`);
};

/**
 * API 비용 통계 조회 (토큰 사용량 및 추정 비용)
 */
export const getApiCostStats = async () => {
  return await makeAuthenticatedRequest('/api/admin/api-keys/cost');
};

/**
 * API 모델별 가격 정보 조회
 */
export const getApiPricing = async (provider = null) => {
  const url = provider 
    ? `/api/admin/api-keys/pricing?provider=${provider}`
    : '/api/admin/api-keys/pricing';
  return await makeAuthenticatedRequest(url);
};

// ============================================================================
// 📊 Analytics API (자체 이벤트 트래킹)
// ============================================================================

/**
 * 최근 이메일 가입자 중 인증 메일 링크까지 누른 비율 (Firebase Auth 기준)
 */
export const getEmailVerificationSummary = async (days = 14) => (
  await makeAuthenticatedRequest(`/api/admin/metrics/email-verification?days=${days}`)
);

/**
 * 프로덕션 매출·제품 가치·퍼널 종합 보고서
 */
export const getRevenueFunnelReport = async ({
  refresh = false,
  includeGa4 = true,
  includeLogs = false,
} = {}) => {
  const params = new URLSearchParams({
    include_ga4: String(includeGa4),
    include_logs: String(includeLogs),
  });
  const path = refresh
    ? '/api/admin/metrics/revenue-funnel/refresh'
    : '/api/admin/metrics/revenue-funnel';
  return await makeAuthenticatedRequest(
    `${path}?${params.toString()}`,
    refresh ? { method: 'POST' } : {}
  );
};

/**
 * 실시간 통계 조회
 */
export const getRealtimeStats = async () => {
  return await makeAuthenticatedRequest('/api/analytics/realtime');
};

/**
 * 결제 퍼널 분석
 */
export const getPaymentFunnelAnalytics = async (period = '7d') => {
  return await makeAuthenticatedRequest(`/api/analytics/funnel/payment?period=${period}`);
};

/**
 * 회원가입 퍼널 분석
 */
export const getSignupFunnelAnalytics = async (period = '7d') => {
  return await makeAuthenticatedRequest(`/api/analytics/funnel/signup?period=${period}`);
};

/**
 * 전환 소스별 분석
 */
export const getConversionSourcesAnalytics = async (period = '30d') => {
  return await makeAuthenticatedRequest(`/api/analytics/conversion/sources?period=${period}`);
};

/**
 * 일별 통계 조회
 */
export const getDailyStatsAnalytics = async (period = '30d') => {
  return await makeAuthenticatedRequest(`/api/analytics/daily-stats?period=${period}`);
};

// ===== 학교 단위 30일 무료체험 검수 =====

/**
 * 학교 무료체험 신청 목록 조회
 * @param {string} status - 'pending' | 'approved' | 'rejected' | 'all'
 */
export const getSchoolTrialApplications = async (status = 'pending', limit = 50) => {
  return await makeAuthenticatedRequest(
    `/api/admin/school-trial/applications?status=${status}&limit=${limit}`
  );
};

/**
 * 전시회 현장 등록자 목록 조회
 */
export const getExhibitionRegistrations = async (limit = 500) => {
  return await makeAuthenticatedRequest(`/api/admin/exhibition/registrations?limit=${limit}`);
};

/**
 * 통합 고객 리드 목록 조회
 * @param {Object} filters - q, source, status, limit, fetch_limit
 */
export const getAdminLeads = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.q) params.append('q', filters.q);
  if (filters.source) params.append('source', filters.source);
  if (filters.status) params.append('status', filters.status);
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.fetch_limit) params.append('fetch_limit', filters.fetch_limit.toString());

  const queryString = params.toString();
  return await makeAuthenticatedRequest(`/api/admin/leads${queryString ? `?${queryString}` : ''}`);
};

/**
 * 학교 무료체험 신청 승인 + 쿠폰 일괄 발급
 */
export const approveSchoolTrialApplication = async (applicationId) => {
  return await makeAuthenticatedRequest(
    `/api/admin/school-trial/applications/${applicationId}/approve`,
    { method: 'POST' }
  );
};

/**
 * 학교 무료체험 신청 반려
 * @param {string} reasonCategory - 'missing_info' | 'suspicious' | 'duplicate' | 'other'
 */
export const rejectSchoolTrialApplication = async (applicationId, reasonCategory, reasonDetail = '') => {
  return await makeAuthenticatedRequest(
    `/api/admin/school-trial/applications/${applicationId}/reject`,
    {
      method: 'POST',
      body: JSON.stringify({
        reason_category: reasonCategory,
        reason_detail: reasonDetail,
      }),
    }
  );
};

/**
 * 대량 구매 주문 목록 조회
 * @param {string} status - 'all' | 'pending' | 'paid' | 'failed'
 */
export const getBulkPurchaseOrders = async (status = 'all', limit = 100) => {
  return await makeAuthenticatedRequest(
    `/api/bulk-purchase/admin/orders?status=${status}&limit=${limit}`
  );
};

/**
 * 대량 구매 환불 처리 미리보기 (dry-run, DB 변경 없음)
 *
 * 응답 구조:
 *  {
 *    success: true,
 *    preview: {
 *      order: {...},
 *      days_since_paid: number,
 *      is_within_refund_window: boolean,
 *      refund_window_days: 7,
 *      toss_payment_status: 'DONE' | 'CANCELED' | ...,
 *      toss_already_canceled: boolean,
 *      refundable_amount: number,
 *      coupons_to_deactivate: [{code, exists, active, current_uses, max_uses}],
 *      users_affected: [{
 *        user_uid, user_email, coupon_code,
 *        current_subscription_type, current_payment_method, current_coupon_source,
 *        current_end_date,
 *        predicted_action: 'subscription_reverted_to_free' | 'subscription_untouched_paid_pro_protected' | 'subscription_untouched_already_free_or_expired',
 *        other_active_coupons: [{code, benefit_end_date}]
 *      }],
 *      warnings: [string]
 *    }
 *  }
 */
export const previewBulkPurchaseRefund = async (orderId) => {
  return await makeAuthenticatedRequest(
    `/api/bulk-purchase/admin/refund-preview/${encodeURIComponent(orderId)}`,
    { method: 'POST' }
  );
};

/**
 * 대량 구매 환불 처리 실행 (비가역, 토스 환불 + 쿠폰 회수 + 알림 발송)
 *
 * @param {string} orderId
 * @param {Object} options
 * @param {string} options.refundReason - 환불 사유 (필수, 1~500자)
 * @param {boolean} options.sendNotifications - 결제자/사용자에게 자동 안내 발송 (default true)
 * @param {boolean} options.forceOutsideWindow - 결제 후 7일 초과 시 강제 진행 (default false)
 */
export const processBulkPurchaseRefund = async (orderId, options = {}) => {
  const {
    refundReason,
    sendNotifications = true,
    forceOutsideWindow = false,
  } = options;

  if (!refundReason || !refundReason.trim()) {
    throw new Error('환불 사유는 필수입니다');
  }

  return await makeAuthenticatedRequest(
    `/api/bulk-purchase/admin/refund-process/${encodeURIComponent(orderId)}`,
    {
      method: 'POST',
      body: JSON.stringify({
        refund_reason: refundReason.trim(),
        send_notifications: sendNotifications,
        force_outside_window: forceOutsideWindow,
      }),
    }
  );
};

// ===== 개인 결제 환불 콘솔 =====

export const lookupAdminRefundUsers = async (query) => {
  return await makeAuthenticatedRequest('/api/admin/refunds/lookup', {
    method: 'POST',
    body: JSON.stringify({ query: query.trim() }),
  });
};

export const previewAdminPersonalRefund = async (
  userId,
  paymentKey,
  {
    refundMode = 'full',
    prorationCutoffDate = null,
  } = {},
) => {
  return await makeAuthenticatedRequest('/api/admin/refunds/preview', {
    method: 'POST',
    body: JSON.stringify({
      user_id: userId,
      payment_key: paymentKey,
      refund_mode: refundMode,
      proration_cutoff_date: refundMode === 'prorated'
        ? prorationCutoffDate
        : null,
    }),
  });
};

export const processAdminPersonalRefund = async ({
  userId,
  paymentKey,
  refundReason,
  forceOutsideWindow = false,
  sendNotification = true,
  refundMode = 'full',
  prorationCutoffDate = null,
  confirmationQuoteFingerprint = null,
}) => {
  return await makeAuthenticatedRequest('/api/admin/refunds/process', {
    method: 'POST',
    body: JSON.stringify({
      user_id: userId,
      payment_key: paymentKey,
      confirmation_payment_key: paymentKey,
      confirmation_quote_fingerprint: confirmationQuoteFingerprint,
      refund_reason: refundReason.trim(),
      refund_mode: refundMode,
      proration_cutoff_date: refundMode === 'prorated'
        ? prorationCutoffDate
        : null,
      force_outside_window: forceOutsideWindow,
      send_notification: sendNotification,
    }),
  });
};

export const resendAdminPaymentDocuments = async ({ userId, paymentKey, toEmail = '' }) => {
  return await makeAuthenticatedRequest(
    `/api/admin/refunds/${encodeURIComponent(paymentKey)}/documents/email`,
    {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, to_email: toEmail }),
    },
  );
};

// ===== Admin Ops Cockpit =====

export const getAdminOperationCases = async ({ status = 'active', query = '', limit = 50, source = 'all' } = {}) => {
  const params = new URLSearchParams({ status, query, limit: String(limit), source });
  return await makeAuthenticatedRequest(`/api/admin/operations/cases?${params.toString()}`);
};

export const createAdminOperationCase = async (payload) => {
  return await makeAuthenticatedRequest('/api/admin/operations/cases', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const getAdminOperationCase = async (caseId) => {
  return await makeAuthenticatedRequest(`/api/admin/operations/cases/${encodeURIComponent(caseId)}`);
};

export const updateAdminOperationCase = async (caseId, payload) => {
  return await makeAuthenticatedRequest(`/api/admin/operations/cases/${encodeURIComponent(caseId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
};

export const deleteAdminOperationCase = async (caseId) => {
  return await makeAuthenticatedRequest(`/api/admin/operations/cases/${encodeURIComponent(caseId)}`, {
    method: 'DELETE',
  });
};

export const addAdminOperationNote = async (caseId, body, type = 'memo') => {
  return await makeAuthenticatedRequest(`/api/admin/operations/cases/${encodeURIComponent(caseId)}/notes`, {
    method: 'POST',
    body: JSON.stringify({ body, type }),
  });
};

export const updateAdminOperationNote = async (caseId, noteId, payload = {}) => {
  const body = typeof payload === 'boolean' ? { completed: payload } : payload;
  return await makeAuthenticatedRequest(
    `/api/admin/operations/cases/${encodeURIComponent(caseId)}/notes/${encodeURIComponent(noteId)}`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
};

export const deleteAdminOperationNote = async (caseId, noteId) => {
  return await makeAuthenticatedRequest(
    `/api/admin/operations/cases/${encodeURIComponent(caseId)}/notes/${encodeURIComponent(noteId)}`,
    {
      method: 'DELETE',
    }
  );
};

export const updateAdminOperationTask = async (caseId, taskId, completed = true, note = '') => {
  return await makeAuthenticatedRequest(
    `/api/admin/operations/cases/${encodeURIComponent(caseId)}/tasks/${encodeURIComponent(taskId)}`,
    {
      method: 'POST',
      body: JSON.stringify({ completed, note }),
    }
  );
};

export const previewAdminOperationDocuments = async (caseId, documentTypes = null) => {
  return await makeAuthenticatedRequest(`/api/admin/operations/cases/${encodeURIComponent(caseId)}/documents/preview`, {
    method: 'POST',
    body: JSON.stringify({ document_types: documentTypes }),
  });
};

export const sendAdminOperationDocumentsEmail = async (caseId, payload = {}) => {
  return await makeAuthenticatedRequest(`/api/admin/operations/cases/${encodeURIComponent(caseId)}/documents/email`, {
    method: 'POST',
    body: JSON.stringify({
      document_types: payload.documentTypes || null,
      subject: payload.subject || '',
      body: payload.body || '',
      dry_run: payload.dryRun !== false,
      include_payment_link: payload.includePaymentLink !== false,
    }),
  });
};

export const ensureAdminOperationPaymentLink = async (caseId) => {
  return await makeAuthenticatedRequest(`/api/admin/operations/cases/${encodeURIComponent(caseId)}/payment-link`, {
    method: 'POST',
  });
};

export const sendAdminOperationPaymentLinkEmail = async (caseId, { dryRun = true } = {}) => {
  return await makeAuthenticatedRequest(`/api/admin/operations/cases/${encodeURIComponent(caseId)}/payment-link/email`, {
    method: 'POST',
    body: JSON.stringify({ dry_run: dryRun }),
  });
};

export const getAdminManagedDocument = async (docType) => {
  return await makeAuthenticatedRequest(`/api/admin/operations/documents/${encodeURIComponent(docType)}`);
};

export const listAdminManagedDocuments = async () => {
  const result = await makeAuthenticatedRequest('/api/admin/operations/documents');
  return result?.documents || [];
};

const authorizedAdminFetch = async (endpoint, options = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error('관리자 인증이 필요합니다');
  const API_BASE_URL = getApiBaseUrl();
  const send = async (forceRefresh) => fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: { Authorization: `Bearer ${await user.getIdToken(forceRefresh)}`, ...options.headers },
  });
  let response = await send(false);
  if (response.status === 401) response = await send(true);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP ${response.status}`);
  }
  return response;
};

export const downloadAdminManagedDocument = async (docType) => {
  const response = await authorizedAdminFetch(
    `/api/admin/operations/documents/${encodeURIComponent(docType)}/file`,
  );
  return await response.blob();
};

export const uploadAdminManagedDocument = async (docType, file, issuedOn = '') => {
  const formData = new FormData();
  formData.append('file', file);
  if (issuedOn) formData.append('issued_on', issuedOn);
  const response = await authorizedAdminFetch(
    `/api/admin/operations/documents/${encodeURIComponent(docType)}`,
    { method: 'POST', body: formData },
  );
  return await response.json();
};

export const issueAdminOperationCoupons = async (caseId, payload = {}) => {
  return await makeAuthenticatedRequest(`/api/admin/operations/cases/${encodeURIComponent(caseId)}/coupons/issue`, {
    method: 'POST',
    body: JSON.stringify({
      series_name: payload.seriesName || '',
      benefit_months: Number(payload.benefitMonths || 12),
      count: payload.count ? Number(payload.count) : null,
      max_uses_per_coupon: Number(payload.maxUsesPerCoupon || 1),
      description: payload.description || '',
      send_email: payload.sendEmail !== false,
      dry_run: payload.dryRun !== false,
    }),
  });
};

export const resendAdminOperationCouponEmail = async (caseId) => {
  return await makeAuthenticatedRequest(`/api/admin/operations/cases/${encodeURIComponent(caseId)}/coupons/resend-email`, {
    method: 'POST',
  });
};

export const sendAdminOperationReply = async (caseId, payload = {}) => {
  return await makeAuthenticatedRequest(`/api/admin/operations/cases/${encodeURIComponent(caseId)}/emails/reply`, {
    method: 'POST',
    body: JSON.stringify({
      subject: payload.subject || '',
      body: payload.body || '',
      to_email: payload.toEmail || null,
    }),
  });
};

/**
 * 리드 CSV 파일 업로드 -> 일괄 발송
 */
export const uploadLeadsCsv = async (file) => {
  const user = auth.currentUser;
  if (!user) throw new Error('관리자 인증이 필요합니다');

  const token = await user.getIdToken(true);
  const API_BASE_URL = getApiBaseUrl();

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/api/leads/upload-csv`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP ${response.status}`);
  }

  return await response.json();
};

const adminService = {
  // 쿠폰 관리
  getAdminCouponStats,
  getAdminCoupons,
  updateCouponStatus,
  deleteCoupon,
  createBulkCoupons,
  
  // 사용자 관리
  getUserCouponStats,
  getActiveUsers,
  getUserCouponHistory,
  
  // 추천 시스템 관리
  getAdminReferralStats,
  getAdminReferralCodes,
  getAdminReferrals,
  getAdminReferralRewards,
  updateRewardStatus,
  
  // 시스템 설정
  getCouponSystemSettings,
  updateCouponSystemSettings,
  getReferralSystemSettings,
  updateReferralSystemSettings,
  
  // 분석 및 리포트
  getCouponUsageTrends,
  getReferralPerformance,
  getUserBehaviorAnalytics,
  
  // 슈퍼 관리자 대시보드
  getApiKeyStatus,
  getConversionAnalytics,
  getRetentionAnalytics,
  getPaymentFlowAnalytics,
  getSystemHealthStatus,
  getAdminPermissions,
  testSlackAlert,
  
  // 그로스 메트릭
  getGrowthMetrics,
  getWeeklyReport,
  getRetentionCohort,
  
  // API 키 상세 관리
  getDetailedApiKeyStatus,
  resetApiKeyCooldown,
  getApiUsageStats,
  getDailyApiUsage,
  getApiCostStats,
  getApiPricing,
  
  // 유틸리티
  checkAdminPermissions,
  getAdminErrorMessage,
  getDateRange,
  exportData,
  getSystemHealth,
  
  // Analytics (자체 이벤트 트래킹)
  getRealtimeStats,
  getPaymentFunnelAnalytics,
  getSignupFunnelAnalytics,
  getConversionSourcesAnalytics,
  getDailyStatsAnalytics,

  // 전시회 현장 등록
  getExhibitionRegistrations,
  getAdminLeads,
  
  // 리드 CSV 발송
  uploadLeadsCsv,

  // 대량구매 환불 자동화
  previewBulkPurchaseRefund,
  processBulkPurchaseRefund,

  // 개인 결제 환불 콘솔
  lookupAdminRefundUsers,
  previewAdminPersonalRefund,
  processAdminPersonalRefund,

  // Admin Ops Cockpit
  getAdminOperationCases,
  createAdminOperationCase,
  getAdminOperationCase,
  updateAdminOperationCase,
  deleteAdminOperationCase,
  addAdminOperationNote,
  updateAdminOperationTask,
  previewAdminOperationDocuments,
  sendAdminOperationDocumentsEmail,
  issueAdminOperationCoupons,
  resendAdminOperationCouponEmail,
  sendAdminOperationReply,
};

export default adminService; 